import React from "react";
import { usePathname, useSearchParams } from "next/navigation";

import useGetSearchParamValues from "@/hooks/useGetSearchParamValues";
import toggleURLSearchParamValue from "@/utils/toggleURLSearchParamValue";

import ExpandableFilterOptions from "@/components/ExpandableFilterOptions";
import { FilterSection } from "@/components/Filters/FilterSection";

import TileLink from "../botanic/TileLink";
import {
  DEFAULT_VISIBLE_TAKE,
  FilterQueryStringKeyProp,
  NoFollowLinkProp,
} from "./DynamicFilters";
import { FilterName, GroupFilter } from "./types";

const TileFilters: React.FC<{
  filter: GroupFilter;
  defaultOpenKey?: string;
  visibleTake?: number;
  sectionPadding?: string;
  noFollowLink?: NoFollowLinkProp;
  filterQueryStringKey?: FilterQueryStringKeyProp;
}> = ({
  filter,
  defaultOpenKey,
  visibleTake = DEFAULT_VISIBLE_TAKE,
  sectionPadding,
  noFollowLink,
  filterQueryStringKey = (filterName) => filterName,
}) => {
  const { values, name, label } = filter;
  const queryStringKey = filterQueryStringKey(name);
  const getSearchParamValues = useGetSearchParamValues();
  const selectedValues: string[] = getSearchParamValues(queryStringKey);

  const visibleOptions = values.slice(0, visibleTake);
  const hiddenOptions = values.slice(visibleTake);

  return (
    <FilterSection
      title={label}
      numberOfItemsSelected={selectedValues.length}
      openDefault={defaultOpenKey === name}
      open={defaultOpenKey === name}
      sectionPadding={sectionPadding}
    >
      <div className="row" data-testid="tile-filters">
        <div className="flex flex-wrap overflow-auto max-h-80">
          <DynamicTileFilter
            filter={{ ...filter, values: visibleOptions }}
            noFollowLink={noFollowLink}
            filterQueryStringKey={filterQueryStringKey}
          />
          {!!hiddenOptions.length && (
            <ExpandableFilterOptions className="ml-sm">
              <DynamicTileFilter
                filter={{ ...filter, values: hiddenOptions }}
                noFollowLink={noFollowLink}
                filterQueryStringKey={filterQueryStringKey}
              />
            </ExpandableFilterOptions>
          )}
        </div>
      </div>
    </FilterSection>
  );
};

const DynamicTileFilter: React.FC<{
  filter: GroupFilter;
  noFollowLink?: NoFollowLinkProp;
  filterQueryStringKey?: FilterQueryStringKeyProp;
}> = ({
  filter,
  noFollowLink,
  filterQueryStringKey = (filterName) => filterName,
}) => {
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const targetURLSearchParams = new URLSearchParams(searchParams?.toString());
  // * Remove pagination params when clicking fulfillment filter link.
  targetURLSearchParams.delete("page");

  const { name, showCount, values } = filter;
  const queryStringKey = filterQueryStringKey(name);
  const getSearchParamValues = useGetSearchParamValues();
  const selectedValues = getSearchParamValues(queryStringKey);

  let rootDir = "";
  switch (name) {
    case FilterName.StrainEffects:
    case "top_strain_effects_included": // TODO special handling for legacy variants api
    case "top_strain_negatives_excluded": // TODO special handling for legacy variants api
      rootDir = "effects";
      break;
    case FilterName.StrainCategory:
      rootDir = "phenotypes";
      break;
    default:
      break;
  }
  const root = rootDir ? `${rootDir}/` : "";

  return (
    <>
      {values?.map(({ label, value, count }) => {
        const href = {
          pathname,
          query: toggleURLSearchParamValue(
            targetURLSearchParams,
            queryStringKey,
            value,
          ).toString(),
        };

        return (
          <div className="col-1/3 mb-sm" key={value}>
            <TileLink
              href={href}
              rel={noFollowLink?.(href) ? "nofollow" : undefined}
              iconPath={`${root}${label.replace(/[_\s]/g, "-").toLowerCase()}.svg`}
              label={label}
              lowercase
              count={count}
              showCount={showCount}
              selected={selectedValues.includes(value)}
            />
          </div>
        );
      })}
    </>
  );
};

export default TileFilters;
